import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, Inject } from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { AlertComponent } from '@vendasta/galaxy/alert/src/alert.component';
import { encodeFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { SalesCoachConfigApiService, SalesCoachUserConfig } from '@vendasta/meeting-analysis';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { StandardIds } from '@galaxy/crm/services/field-service';
import {
  CONTEXT_DATA_TOKEN,
  ContextData,
  CRM_ACTIVITY_FEED_PAGE_TOKEN,
  MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN,
} from '../data-providers/providers';
import { MeetingSchedulerStoreService } from '../data-providers/meeting-scheduler-store.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AvailableServices, MeetingViewModelService } from '@vendasta/integrations';

@Component({
  selector: 'meeting-scheduler-my-meetings',
  templateUrl: './my-meetings.component.html',
  styleUrl: './my-meetings.component.scss',
  standalone: true,
  imports: [CommonModule, MatTabsModule, TranslateModule, GalaxyPageModule, RouterModule, AlertComponent],
})
export class MyMeetingsComponent {
  private readonly BOOKINGS_ALERT_KEY = 'my-meetings-bookings-alert-dismissed';
  private readonly RECORDINGS_ALERT_KEY = 'my-meetings-recordings-alert-dismissed';

  namespace$: Observable<string>;
  userId$: Observable<string>;
  salesCoachUserConfig$: Observable<SalesCoachUserConfig>;

  // Connection status observables
  isGoogleMeetConnected$: Observable<boolean>;
  isMicrosoftConnected$: Observable<boolean>;
  isZoomConnected$: Observable<boolean>;

  // Separate observables for calendar and meeting app disconnections
  isCalendarDisconnected$: Observable<boolean>;
  isMeetingAppDisconnected$: Observable<boolean>;
  hasDisconnectedIntegrations$: Observable<boolean>;

  // This magic string is so that we don't have to import all of meeting scheduler into business app proper as meeting scheduler doesn't currently
  // have a `static` module to avoid this.
  constructor(
    @Inject('show_my_meetings_recording_tab$') readonly showMyMeetingsRecordingTab$: Observable<boolean>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly salesCoachConfigService: SalesCoachConfigApiService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    @Inject(CONTEXT_DATA_TOKEN) private readonly contextData$: Observable<ContextData>,
    @Inject(MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN) private readonly settingsLink$: Observable<string>,
    @Inject(CRM_ACTIVITY_FEED_PAGE_TOKEN) public readonly activityFeedPageLink$: Observable<string>,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly meetingViewModelService: MeetingViewModelService,
  ) {
    this.initializeObservables();
  }

  private initializeObservables(): void {
    this.namespace$ = this.contextData$.pipe(map((data) => data.namespace));

    this.userId$ = of(this.meetingSchedulerStoreService.getUserId());

    this.salesCoachUserConfig$ = combineLatest([this.namespace$, this.userId$]).pipe(
      switchMap(([namespace, userId]) => {
        return this.salesCoachConfigService.getUserConfig({
          namespace: namespace,
          userId: userId,
        });
      }),
      map((resp) => {
        const config = resp?.salesCoachUserConfig;
        return config;
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MEETING_SCHEDULER.SALES_COACH_CONFIG.LOAD_ERROR'),
          );
        }
        return of({ joinMeetings: false } as SalesCoachUserConfig);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Initialize integration connection status observables
    this.isGoogleMeetConnected$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.GOOGLE_MEET);
    this.isMicrosoftConnected$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.MICROSOFT);
    this.isZoomConnected$ = this.meetingViewModelService.isServiceConnected$(AvailableServices.ZOOM);

    // Observable to check if calendar is disconnected (no calendar integration)
    this.isCalendarDisconnected$ = combineLatest([this.isGoogleMeetConnected$, this.isMicrosoftConnected$]).pipe(
      map(([googleConnected, microsoftConnected]) => {
        // Calendar is disconnected if neither Google nor Microsoft (both provide calendar) is connected
        const hasCalendarIntegration = googleConnected || microsoftConnected;
        return !hasCalendarIntegration;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Observable to check if meeting app is disconnected (no video conferencing)
    this.isMeetingAppDisconnected$ = combineLatest([
      this.isGoogleMeetConnected$,
      this.isMicrosoftConnected$,
      this.isZoomConnected$,
    ]).pipe(
      map(([googleConnected, microsoftConnected, zoomConnected]) => {
        // Meeting app is disconnected if none of the video conferencing services is connected
        const hasMeetingAppIntegration = googleConnected || microsoftConnected || zoomConnected;
        return !hasMeetingAppIntegration;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Combined observable for overall disconnection status (for styling purposes)
    this.hasDisconnectedIntegrations$ = combineLatest([
      this.isCalendarDisconnected$,
      this.isMeetingAppDisconnected$,
    ]).pipe(
      map(([calendarDisconnected, meetingAppDisconnected]) => {
        return calendarDisconnected || meetingAppDisconnected;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  get showBookingsAlert(): boolean {
    return !localStorage.getItem(this.BOOKINGS_ALERT_KEY);
  }

  get showRecordingsAlert(): boolean {
    return !localStorage.getItem(this.RECORDINGS_ALERT_KEY);
  }

  onBookingsAlertClose(): void {
    localStorage.setItem(this.BOOKINGS_ALERT_KEY, 'true');
  }

  onRecordingsAlertClose(): void {
    localStorage.setItem(this.RECORDINGS_ALERT_KEY, 'true');
  }

  navigateToCrmRecordedMeetings(): void {
    const filters = [
      {
        fieldId: StandardIds.ActivityMeetingStatus,
        filterId: 'activity-meeting-status-filter',
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
        values: [{ string: 'Completed' }],
      },
      {
        fieldId: StandardIds.ActivitySourceName,
        filterId: 'activity-source-name-filter',
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
        values: [{ string: 'Meeting Analysis' }],
      },
    ];
    const filterParam = encodeFilters(filters);
    this.activityFeedPageLink$.pipe(take(1)).subscribe((activityFeedLink) => {
      this.router.navigate([`${activityFeedLink}`], { queryParams: { filter: filterParam } });
    });
  }

  navigateToSettings(): void {
    this.settingsLink$.pipe(take(1)).subscribe((settingsUrl) => {
      this.router.navigateByUrl(settingsUrl);
    });
  }

  reconnectIntegrations(): void {
    // Navigate to meeting-settings page for reconnecting calendar/meeting apps
    // Uses the same approach as navigateToSettings() to get the proper URL from DI token
    this.settingsLink$.pipe(take(1)).subscribe((settingsUrl) => {
      this.router.navigateByUrl(settingsUrl);
    });
  }

  get isBookingsActive(): boolean {
    return this.router.url.includes('/bookings');
  }

  get isRecordingsActive(): boolean {
    return this.router.url.includes('/recordings');
  }

  get hasAnyAlert(): boolean {
    return (this.isBookingsActive && this.showBookingsAlert) || (this.isRecordingsActive && this.showRecordingsAlert);
  }
}
